import React from 'react';
import { LOGO_HEIGHT, LOGO_PATHS, LOGO_VIEWBOX, LOGO_WIDTH, TILE_MARK_SHARE, TILE_RADIUS, TILE_SIZE } from './logoMark';

type LogoProps = {
  /** sm: 32 px mark (header). lg: 48 px mark (footer, 404). xl: 160 px mark (closing call to action). */
  size?: 'sm' | 'lg' | 'xl';
  /** Show the "LulPay" wordmark next to the mark. */
  wordmark?: boolean;
  /** Tile colour: teal by default, teal deep on deep-teal surfaces (D5). */
  surface?: 'teal' | 'deep';
  /** Hide from screen readers when another LulPay name is already on screen. */
  decorative?: boolean;
  className?: string;
};

const markWidth = TILE_SIZE * TILE_MARK_SHARE;
const markHeight = (markWidth * LOGO_HEIGHT) / LOGO_WIDTH;

/** The LulPay mark on its teal tile, with an optional Inter 700 wordmark that takes the text colour. */
const Logo: React.FC<LogoProps> = ({ size = 'sm', wordmark = true, surface = 'teal', decorative = false, className }) => {
  const classes = ['ui-logo', `ui-logo--${size}`];
  if (className) classes.push(className);
  // With the wordmark, the visible text names the logo; without it, the mark carries the name.
  const named = !wordmark && !decorative;
  return (
    <span className={classes.join(' ')} aria-hidden={decorative ? true : undefined}>
      <svg
        className="ui-logo__mark"
        viewBox={`0 0 ${TILE_SIZE} ${TILE_SIZE}`}
        role={named ? 'img' : undefined}
        aria-label={named ? 'LulPay' : undefined}
        aria-hidden={named ? undefined : true}
        focusable="false"
      >
        <rect
          width={TILE_SIZE}
          height={TILE_SIZE}
          rx={TILE_RADIUS}
          style={{ fill: surface === 'deep' ? 'var(--teal-deep)' : 'var(--teal)' }}
        />
        <svg
          x={(TILE_SIZE - markWidth) / 2}
          y={(TILE_SIZE - markHeight) / 2}
          width={markWidth}
          height={markHeight}
          viewBox={LOGO_VIEWBOX}
        >
          {LOGO_PATHS.map((d) => (
            <path key={d} d={d} style={{ fill: 'var(--white)' }} />
          ))}
        </svg>
      </svg>
      {wordmark ? <span className="ui-logo__word">LulPay</span> : null}
    </span>
  );
};

export default Logo;
